import { Hub } from "aws-amplify/utils";

export type OAuthRedirectState = {
  returnTo?: string | null;
  defaultModule?: string | null;
  mode?: "signin" | "signup" | null;
  persona?: "parent" | "staff" | "administrator" | null;
  postAuthPersonaSelection?: boolean;
  schoolId?: string | null;
  savedAt: number;
};

const STORAGE_KEY = "ystnj-oauth-redirect-state";
const MAX_STATE_AGE_MS = 30 * 60 * 1000;

let listenerRegistered = false;

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeMode = (value: unknown): OAuthRedirectState["mode"] => {
  return value === "signup" ? "signup" : "signin";
};

const normalizePersona = (value: unknown): OAuthRedirectState["persona"] => {
  if (value === "parent" || value === "staff" || value === "administrator") {
    return value;
  }
  return null;
};

const normalizeState = (value: unknown): OAuthRedirectState | null => {
  if (!value || typeof value !== "object") {
    return null;
  }
  const input = value as Record<string, unknown>;
  const savedAt = Number(input.savedAt);
  if (!Number.isFinite(savedAt) || savedAt <= 0) {
    return null;
  }
  return {
    returnTo: normalizeString(input.returnTo),
    defaultModule: normalizeString(input.defaultModule),
    mode: normalizeMode(input.mode),
    persona: normalizePersona(input.persona),
    postAuthPersonaSelection: input.postAuthPersonaSelection === true,
    schoolId: normalizeString(input.schoolId),
    savedAt,
  };
};

const parseSerializedState = (
  value: string | null,
): OAuthRedirectState | null => {
  if (!value) {
    return null;
  }
  try {
    return normalizeState(JSON.parse(value));
  } catch {
    return null;
  }
};

const isFresh = (state: OAuthRedirectState): boolean =>
  Date.now() - state.savedAt <= MAX_STATE_AGE_MS;

export const clearOAuthRedirectState = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("[auth] Unable to clear OAuth redirect state:", error);
  }
};

export const persistOAuthRedirectState = (
  input: Omit<OAuthRedirectState, "savedAt">,
): OAuthRedirectState => {
  const payload: OAuthRedirectState = {
    returnTo: normalizeString(input.returnTo),
    defaultModule: normalizeString(input.defaultModule),
    mode: normalizeMode(input.mode),
    persona: normalizePersona(input.persona),
    postAuthPersonaSelection: input.postAuthPersonaSelection === true,
    schoolId: normalizeString(input.schoolId),
    savedAt: Date.now(),
  };
  if (typeof window === "undefined") {
    return payload;
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("[auth] Unable to persist OAuth redirect state:", error);
  }
  return payload;
};

export const serializeOAuthRedirectState = (
  input: Omit<OAuthRedirectState, "savedAt">,
): string => JSON.stringify(persistOAuthRedirectState(input));

export const consumeOAuthRedirectState = (): OAuthRedirectState | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const parsed = parseSerializedState(
      window.sessionStorage.getItem(STORAGE_KEY),
    );
    window.sessionStorage.removeItem(STORAGE_KEY);
    return parsed && isFresh(parsed) ? parsed : null;
  } catch (error) {
    console.warn("[auth] Unable to read OAuth redirect state:", error);
    return null;
  }
};

export const ensureOAuthRedirectStateListener = (): void => {
  if (listenerRegistered || typeof window === "undefined") {
    return;
  }
  listenerRegistered = true;
  Hub.listen("auth", ({ payload }) => {
    if (
      payload?.event !== "customOAuthState" ||
      typeof payload.data !== "string"
    ) {
      return;
    }
    const parsed = parseSerializedState(payload.data);
    if (!parsed || !isFresh(parsed)) {
      return;
    }
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch (error) {
      console.warn(
        "[auth] Unable to restore OAuth redirect state from customOAuthState:",
        error,
      );
    }
  });
};
